import { baseApi } from '@alltrails/redux-helpers';
import type Trail from '@alltrails/core/types/Trail';
import { Map } from '@alltrails/shared/types/map';
import { atMapsToGeojson } from '../utils/legacyGeoJSONConversions';
import getCoordinatesFromFeatures from '../utils/getCoordinatesFromFeatures';
import { LngLat } from '../types/Geo';
import TrailSurfaceTypesData from '../types/TrailSurfaceTypesData';
import TrailSurfaceTypesParams from '../types/TrailSurfaceTypesParams';

type TrailPolylineResponse = { trails: Trail[] };
type MapPolylineResponse = { maps: Map[] };

type GetPolylineParams = {
  baseUrl?: string;
  trailId?: string | number;
  mapId?: string | number;
};

export const mapsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getPolyline: builder.query<LngLat[], GetPolylineParams>({
      query: ({ baseUrl = '', trailId, mapId }) => {
        if (!trailId && !mapId) {
          throw new Error('Either trailId or mapId must be provided');
        }

        if (trailId) {
          const url = `${baseUrl}/api/alltrails/trails/${trailId}`;
          const method = 'get';
          return { url, method, config: { params: { detail: 'offline', include_pending: true } } };
        }

        const url = `${baseUrl}/api/alltrails/maps/${mapId}`;
        const method = 'get';
        return { url, method, config: { params: { detail: 'deep' } } };
      },
      transformResponse: (response: TrailPolylineResponse | MapPolylineResponse, _, arg) => {
        const map = arg.trailId ? (response as TrailPolylineResponse).trails[0].defaultMap : (response as MapPolylineResponse).maps[0];
        const geoJson = atMapsToGeojson([map]);
        const newRouteCoordinates = getCoordinatesFromFeatures(geoJson) || [];
        return response ? newRouteCoordinates : [];
      }
    }),
    getTrailSurfaceTypes: builder.query<TrailSurfaceTypesData, TrailSurfaceTypesParams>({
      query: ({ includeSegments, showHidden, trailId }) => {
        return { url: `/api/alltrails/trails/${trailId}/surface_types`, config: { params: { includeSegments, showHidden } } };
      }
    })
  })
});

export const { useGetPolylineQuery, useGetTrailSurfaceTypesQuery } = mapsApi;
